import { RefObject, useReducer, useRef, useState } from "react";
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Snackbar,
} from "@mui/material";
import {
  RecurrenceEditScope,
  WorkoutFormModel,
} from "../../lib/workout-form/transformers";
import workoutReducer from "../../reducers/workoutReducer";
import ViewWorkout from "./view-workout";
import { useLocation, useNavigate } from "react-router-dom";
import { Workout } from "../../providers/workouts";
import { validateAll } from "../../lib/workout-form/validators";

type Props = {
  onSubmit: (
    workoutForm: WorkoutFormModel,
    recurrenceCount: number,
    scope: RecurrenceEditScope
  ) => Promise<Workout>;
  onDelete: (scope: RecurrenceEditScope) => Promise<any>;
  workoutForm: WorkoutFormModel;
  edit: boolean;
  isRecurringSeriesWorkout: boolean;
};

type PendingRecurringAction = "save" | "delete" | null;

function getSaveErrorMessage(error: unknown) {
  const firebaseError = error as { code?: string } | null;

  if (
    firebaseError?.code === "permission-denied"
  ) {
    return "Du saknar behörighet att skapa eller ändra pass.";
  }

  return "Passet kunde inte sparas. Försök igen.";
}

//Set up form with default values. Handle submit
const LogicWorkout = ({
  onDelete,
  onSubmit,
  workoutForm,
  edit,
  isRecurringSeriesWorkout,
}: Props) => {
  const handleErrorRefs: RefObject<(() => void)[]> = useRef([]);
  const [workout, dispatch] = useReducer(workoutReducer, workoutForm);
  const navigate = useNavigate();
  const location = useLocation();
  const returnTo = (location.state as { returnTo?: string } | null)?.returnTo;
  const [loading, setLoading] = useState(false);
  const [recurrenceCount, setRecurrenceCount] = useState(1);
  const [pendingRecurringAction, setPendingRecurringAction] =
    useState<PendingRecurringAction>(null);
  const [saveError, setSaveError] = useState<string | null>(null);

  const executeSubmit = async (scope: RecurrenceEditScope) => {
    setLoading(true);
    setSaveError(null);

    try {
      await onSubmit(workout, recurrenceCount, scope);
      navigate(returnTo ?? "/")
    } catch (error) {
      console.error("Could not save workout", error);
      setSaveError(getSaveErrorMessage(error));
    } finally {
      setLoading(false);
    }
  };

  const executeDelete = async (scope: RecurrenceEditScope) => {
    setLoading(true);
    try {
      await onDelete(scope);
      navigate(returnTo ?? "/");
    } catch (error) {
      console.log(error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async () => {
    const errors = validateAll(workout);
    handleErrorRefs.current?.forEach((handleError) => {
      handleError();
    });
    if (errors.length > 0) {
      setSaveError("Fyll i de obligatoriska fälten innan du sparar.");
      return;
    }

    if (edit && isRecurringSeriesWorkout) {
      setPendingRecurringAction("save");
      return;
    }

    await executeSubmit("single");
  };

  const handleDelete = async () => {
    if (edit && isRecurringSeriesWorkout) {
      setPendingRecurringAction("delete");
      return;
    }

    await executeDelete("single");
  };

  const handleRecurringAction = async (scope: RecurrenceEditScope) => {
    const currentAction = pendingRecurringAction;
    setPendingRecurringAction(null);

    if (currentAction === "save") {
      await executeSubmit(scope);
      return;
    }

    if (currentAction === "delete") {
      await executeDelete(scope);
    }
  };

  return (
    <>
      <ViewWorkout
        handleErrorRefs={handleErrorRefs}
        onDelete={handleDelete}
        onSubmit={handleSubmit}
        dispatch={dispatch}
        edit={edit}
        loading={loading}
        recurrenceCount={recurrenceCount}
        setRecurrenceCount={setRecurrenceCount}
        workout={workout}></ViewWorkout>
      <Dialog
        open={pendingRecurringAction !== null}
        onClose={() => setPendingRecurringAction(null)}>
        <DialogTitle>
          {pendingRecurringAction === "save"
            ? "Ändra återkommande pass"
            : "Ta bort återkommande pass"}
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            {pendingRecurringAction === "save"
              ? "Det här passet tillhör en serie. Ska ändringen gälla bara detta pass eller detta pass och alla efterföljande i serien?"
              : "Det här passet tillhör en serie. Ska borttagningen gälla bara detta pass eller detta pass och alla efterföljande i serien?"}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPendingRecurringAction(null)}>Avbryt</Button>
          <Button onClick={() => handleRecurringAction("single")}>
            Bara detta pass
          </Button>
          <Button
            color={pendingRecurringAction === "delete" ? "error" : "primary"}
            onClick={() => handleRecurringAction("following")}>
            Detta och efterföljande
          </Button>
        </DialogActions>
      </Dialog>
      <Snackbar
        open={Boolean(saveError)}
        autoHideDuration={6000}
        onClose={() => setSaveError(null)}>
        <Alert severity="error" onClose={() => setSaveError(null)}>
          {saveError}
        </Alert>
      </Snackbar>
    </>
  );
};

export default LogicWorkout;
